import { type ReactNode, useMemo } from "react";

import {
  Field as ChakraField,
  Portal,
  Select,
  type SelectValueChangeDetails,
  createListCollection,
} from "@chakra-ui/react";

export type SelectOption<T> = {
  label: ReactNode;
  value: "null" | T;
};

type Props<T> = {
  disabled?: boolean;
  errorText?: string;
  helperText?: string;
  label?: string;
  name?: string;
  options: SelectOption<T>[];
  placeholder?: string;
  value?: T;
  isInvalid: boolean;
  isRequired?: boolean;
  onBlur?: () => void;
  onChange?: (value: T) => void;
};

export const SelectField = <T extends string>({
  disabled,
  errorText,
  helperText,
  label,
  name,
  options,
  placeholder,
  value,
  isInvalid,
  isRequired,
  onBlur,
  onChange,
}: Props<T>) => {
  const collection = useMemo(
    () =>
      createListCollection({
        items: options,
      }),
    [options],
  );

  const handleValueChange = ({
    value,
  }: SelectValueChangeDetails<SelectOption<T>>) => {
    const next = value[0] as T;
    onChange?.(next);
  };

  return (
    <ChakraField.Root
      disabled={disabled}
      invalid={isInvalid}
      required={isRequired}
    >
      {label && (
        <ChakraField.Label>
          {label}
          <ChakraField.RequiredIndicator />
        </ChakraField.Label>
      )}
      <Select.Root
        collection={collection}
        name={name}
        value={value ? [value] : []}
        width="full"
        onInteractOutside={onBlur}
        onValueChange={handleValueChange}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger backgroundColor="whiteAlpha.0" borderRadius="xl">
            <Select.ValueText placeholder={placeholder} />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content>
              {options.map((item) => (
                <Select.Item key={item.value} item={item}>
                  {item.label}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
      {isInvalid && (
        <ChakraField.ErrorText>{errorText} &nbsp;</ChakraField.ErrorText>
      )}
      {!isInvalid && helperText && (
        <ChakraField.HelperText>{helperText} &nbsp;</ChakraField.HelperText>
      )}
    </ChakraField.Root>
  );
};
